import { computed, inject, Injectable, signal } from '@angular/core';

import confetti from 'canvas-confetti';

import { ProductUpdatesStateService } from './product-updates-state.service';
import { TimelineDataService } from './timeline-data.service';
import type { ProductUpdate } from '../models/product-update.model';

export type RewindPhase = 'intro' | 'timeline' | 'stats' | 'outro';

export interface RewindStats {
  features: number;
  improvements: number;
  integrations: number;
  comingSoon: number;
  total: number;
  // Customer stats
  newCandidates: number;
  jobsPublished: number;
  careerVisitors: number;
}

@Injectable({ providedIn: 'root' })
export class RewindService {
  private readonly stateService = inject(ProductUpdatesStateService);
  private readonly timelineDataService = inject(TimelineDataService);

  // Total number of stats to reveal (5 product stats + 3 customer stats)
  private readonly totalStats = 8;

  /**
   * Posts flattened in timeline display order (quarter → month → post)
   * This matches the global index used in the timeline component
   */
  private readonly timelineOrderedPosts = computed<ProductUpdate[]>(() => {
    const updates = this.stateService.productUpdates();
    const quarterGroups = this.timelineDataService.groupPostsByMonth(updates);
    return quarterGroups.flatMap(q => q.months.flatMap(m => m.posts));
  });

  // Core state signals - check URL immediately to prevent flash
  public readonly enabled = signal(this.checkInitialRewindState());
  public readonly phase = signal<RewindPhase>('intro');
  public readonly currentPostIndex = signal(-1);
  public readonly currentStatIndex = signal(0);
  public readonly companyName = signal<string | null>(null);

  // Hardcoded stats (as per user decision)
  public readonly summaryStats = signal<RewindStats>({
    features: 44,
    improvements: 67,
    integrations: 21,
    comingSoon: 11,
    total: 143,
    // Customer stats
    newCandidates: 22540193,
    jobsPublished: 306615,
    careerVisitors: 77426872,
  });

  // Computed values
  public readonly totalPosts = computed(() => this.stateService.filteredUpdates().length);

  public readonly progressPercent = computed(() => {
    const total = this.totalPosts();
    if (total === 0) return 0;
    const current = this.currentPostIndex() + 1;
    return Math.round((current / total) * 100);
  });

  /**
   * Enable rewind mode and start from intro
   */
  public enableRewind(): void {
    this.enabled.set(true);
    this.phase.set('intro');
    this.currentPostIndex.set(-1);
    this.currentStatIndex.set(0);
  }

  /**
   * Disable rewind mode and reset state
   */
  public disableRewind(): void {
    this.enabled.set(false);
    this.phase.set('intro');
    this.currentPostIndex.set(-1);
    this.currentStatIndex.set(0);
  }

  /**
   * Set company name for personalized intro
   */
  public setCompanyName(name: string | null): void {
    this.companyName.set(name);
  }

  /**
   * Advance to next step in the presentation
   */
  public nextStep(): void {
    const currentPhase = this.phase();

    switch (currentPhase) {
      case 'intro':
        // Move from intro to timeline, reveal first post
        this.phase.set('timeline');
        this.currentPostIndex.set(0);
        // Check if first post is featured
        this.checkAndTriggerFeaturedConfetti(0);
        break;

      case 'timeline':
        const nextPostIndex = this.currentPostIndex() + 1;
        if (nextPostIndex < this.totalPosts()) {
          // Reveal next post
          this.currentPostIndex.set(nextPostIndex);
          // Trigger confetti for featured posts with media
          this.checkAndTriggerFeaturedConfetti(nextPostIndex);
        } else {
          // All posts revealed, move to stats (show summary first, no stats yet)
          this.phase.set('stats');
          this.currentStatIndex.set(0);
        }
        break;

      case 'stats':
        const nextStatIndex = this.currentStatIndex() + 1;
        if (nextStatIndex <= this.totalStats) {
          this.currentStatIndex.set(nextStatIndex);

          // Trigger confetti when Total Updates (stat 5) is revealed
          if (nextStatIndex === 5) {
            this.triggerFireworksConfetti();
          }
        } else {
          // All stats revealed, move to outro
          this.phase.set('outro');
        }
        break;

      case 'outro':
        // End of presentation - do nothing or could restart
        break;
      default:
        break;
    }
  }

  /**
   * Go back to previous step in the presentation
   */
  public prevStep(): void {
    const currentPhase = this.phase();

    switch (currentPhase) {
      case 'intro':
        // Already at beginning, do nothing
        break;

      case 'timeline':
        const prevPostIndex = this.currentPostIndex() - 1;
        if (prevPostIndex >= 0) {
          // Go to previous post
          this.currentPostIndex.set(prevPostIndex);
        } else {
          // At first post, go back to intro
          this.phase.set('intro');
          this.currentPostIndex.set(-1);
        }
        break;

      case 'stats':
        const prevStatIndex = this.currentStatIndex() - 1;
        if (prevStatIndex >= 0) {
          // Go to previous stat (or back to summary at index 0)
          this.currentStatIndex.set(prevStatIndex);
        } else {
          // At summary (index 0), go back to timeline (last post)
          this.phase.set('timeline');
        }
        break;

      case 'outro':
        // Go back to stats (all revealed)
        this.phase.set('stats');
        this.currentStatIndex.set(this.totalStats);
        break;
      default:
        break;
    }
  }

  /**
   * Jump to a specific post index
   */
  public goToPost(index: number): void {
    if (index >= 0 && index < this.totalPosts()) {
      this.phase.set('timeline');
      this.currentPostIndex.set(index);
    }
  }

  /**
   * Check if a post should be revealed (visible)
   */
  public isPostRevealed(globalIndex: number): boolean {
    const currentPhase = this.phase();

    // In intro phase, no posts are revealed
    if (currentPhase === 'intro') return false;

    // In stats/outro phase, all posts are revealed
    if (currentPhase === 'stats' || currentPhase === 'outro') return true;

    // In timeline phase, show posts up to current index
    return globalIndex <= this.currentPostIndex();
  }

  /**
   * Check if a post is the currently active (highlighted) one
   */
  public isPostActive(globalIndex: number): boolean {
    return this.phase() === 'timeline' && globalIndex === this.currentPostIndex();
  }

  /**
   * Check if a stat is currently active (being revealed)
   */
  public isStatActive(statIndex: number): boolean {
    return this.phase() === 'stats' && statIndex === this.currentStatIndex();
  }

  /**
   * Check if a stat should be visible (revealed)
   */
  public isStatRevealed(statIndex: number): boolean {
    const currentPhase = this.phase();

    // Stats only visible in stats phase
    if (currentPhase !== 'stats' && currentPhase !== 'outro') return false;

    // In outro, all stats are revealed
    if (currentPhase === 'outro') return true;

    // In stats phase, show stats up to current index
    return statIndex <= this.currentStatIndex();
  }

  /**
   * Check if rewind mode should be enabled on initial load
   * This runs synchronously before Angular routing to prevent flash
   */
  private checkInitialRewindState(): boolean {
    if (typeof window === 'undefined') return false;
    // Normalize malformed URLs where ? is used instead of & (e.g., ?rewind=1?company=X)
    const normalizedSearch = window.location.search.replace(/\?/g, '&').replace('&', '?');
    const params = new URLSearchParams(normalizedSearch);
    return params.get('rewind') === '1';
  }

  /**
   * Check if the current post is featured with media and trigger confetti
   */
  private checkAndTriggerFeaturedConfetti(postIndex: number): void {
    // Use timeline-ordered posts to match the global index from timeline component
    const posts = this.timelineOrderedPosts();
    const post = posts[postIndex];
    if (!post) return;

    // Check if post has media (featured image or video) with English fallback
    const locale = this.stateService.currentLocale();
    const translation =
      post.translations.find(t => t.locale === locale) ??
      post.translations.find(t => t.locale === post.primaryLocale) ??
      post.translations[0];
    const englishTranslation = post.translations.find(t => t.locale === 'en');

    const featuredImage = translation?.featuredImage || englishTranslation?.featuredImage;
    const youtubeUrl = translation?.youtubeUrl || englishTranslation?.youtubeUrl;
    const hasMedia = !!(featuredImage || youtubeUrl);
    const isNew = post.updateCategories.includes('New');
    const isFeaturedWithMedia = hasMedia && (!!post.isFeatured || isNew);

    if (isFeaturedWithMedia) {
      this.triggerFeaturedConfetti();
    }
  }

  /**
   * Trigger a light confetti burst for featured posts
   */
  private triggerFeaturedConfetti(): void {
    // Single burst from center-top, falling down
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x: 0.5, y: 0.3 },
      colors: ['#5A1CB7', '#E23393', '#FFE400', '#FFBD00'],
      ticks: 200,
      gravity: 1.2,
      scalar: 1.2,
    });
  }

  /**
   * Trigger fireworks confetti effect
   */
  private triggerFireworksConfetti(): void {
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = (): void => {
      // Launch from left edge
      confetti({
        particleCount: 7,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#5A1CB7', '#E23393', '#FFE400', '#FFBD00'],
      });

      // Launch from right edge
      confetti({
        particleCount: 7,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#5A1CB7', '#E23393', '#FFE400', '#FFBD00'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }
}
